import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { apiService } from '@/services/api';
import { OrderOut, EventOut, EventNightOut, EventNightSummaryOut } from '@/types/api';
import { canAccessAllEvents } from '@/utils/roleUtils';
import Card from '@/components/atoms/Card';
import Button from '@/components/atoms/Button';
import { FormField } from '@/components/molecules/FormField';
import { SearchableSelect } from '@/components/molecules/SearchableSelect';
import { useDateFormatter } from '@/hooks/useDateFormatter';
import { usePriceFormat } from '@/hooks/usePriceFormat';
import OrderActivityLog from '@/components/molecules/OrderActivityLog';
import './OrderManagement.scss';

interface OrderFilters {
  event_id?: number;
  status?: string;
  date_from?: string;
  date_to?: string;
  occurrence_start?: string;
}

// Orders of one night are all loaded at once (the list default is 100)
const NIGHT_ORDERS_LIMIT = 5000;

// 'YYYY-MM-DD' from a date input -> start of that day in the browser's time zone, as UTC ISO
const localDayStart = (day: string, addDays = 0) => {
  const date = new Date(`${day}T00:00:00`);
  date.setDate(date.getDate() + addDays);
  return date.toISOString();
};

const OrderManagement: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { formatPrice, formatWithTax } = usePriceFormat();
  const { user } = useAuth();
  const { formatOrderDate, formatDetailedOrderDate } = useDateFormatter();
  const [orders, setOrders] = useState<OrderOut[]>([]);
  const [events, setEvents] = useState<EventOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<OrderFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<OrderOut | null>(null);
  const [nights, setNights] = useState<EventNightOut[]>([]);
  const [nightSummary, setNightSummary] = useState<EventNightSummaryOut | null>(null);
  // Only the latest fetch may set the list: a slower earlier one (e.g. all events)
  // must not overwrite the result of newer filters
  const fetchSeq = useRef(0);

  // occurrence_start is the event's local time with no zone: show it as-is, never shifted
  const formatNight = (value: string) => {
    const [day, time = '00:00'] = value.split('T');
    const [year, month, date] = day.split('-').map(Number);
    const [hours, minutes] = time.split(':').map(Number);
    return new Date(year, month - 1, date, hours, minutes).toLocaleString(i18n.language, {
      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  // Fetch orders and events for the current organizer or all events for super admin
  const fetchData = useCallback(async () => {
    const seq = ++fetchSeq.current;
    try {
      setLoading(true);
      
      // Fetch events first
      const eventsData = canAccessAllEvents(user)
        ? await apiService.getEvents()
        : await apiService.getMyManagedEvents();
      setEvents(eventsData);
      
      const dateParams = {
        created_from: filters.date_from ? localDayStart(filters.date_from) : undefined,
        created_to: filters.date_to ? localDayStart(filters.date_to, 1) : undefined,
      };

      // Fetch orders with filters - only from organizer's events
      if (filters.event_id) {
        // Fetch orders for specific event
        const ordersData = await apiService.getOrders({
          event_id: filters.event_id,
          status: filters.status,
          ...dateParams,
          ...(filters.occurrence_start
            ? { occurrence_start: filters.occurrence_start, limit: NIGHT_ORDERS_LIMIT }
            : {}),
        });
        if (seq === fetchSeq.current) setOrders(ordersData);
      } else {
        // Fetch orders for all organizer's events
        const allOrders: OrderOut[] = [];
        for (const event of eventsData) {
          const eventOrders = await apiService.getOrders({
            event_id: event.id,
            status: filters.status,
            ...dateParams,
          });
          allOrders.push(...eventOrders);
        }
        if (seq === fetchSeq.current) setOrders(allOrders);
      }
    } catch (err: any) {
      if (seq === fetchSeq.current) setError(err.message || t('errors.general'));
    } finally {
      if (seq === fetchSeq.current) setLoading(false);
    }
  }, [filters, t, user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Nights of the selected event that have orders, for the "Event night" filter
  useEffect(() => {
    setNights([]);
    if (!filters.event_id) return;
    apiService.getEventNights(filters.event_id).then(setNights).catch(() => setNights([]));
  }, [filters.event_id]);

  // Sales summary of the selected night
  useEffect(() => {
    setNightSummary(null);
    if (!filters.event_id || !filters.occurrence_start) return;
    apiService.getEventNightSummary(filters.event_id, filters.occurrence_start)
      .then(setNightSummary)
      .catch((err: any) => setError(err.message || t('errors.general')));
  }, [filters.event_id, filters.occurrence_start, t]);

  const handleFilterChange = (field: keyof OrderFilters, value: string | number | undefined) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      // A night belongs to one event
      ...(field === 'event_id' ? { occurrence_start: undefined } : {}),
    }));
  };

  const handleApplyFilters = () => {
    fetchData();
  };

  const handleClearFilters = () => {
    setFilters({});
    fetchData();
  };

  const handleUpdateOrderStatus = async (orderId: number, newStatus: string) => {
    try {
      await apiService.updateOrderStatus(orderId, newStatus);
      await fetchData();
    } catch (err: any) {
      setError(err.message || t('errors.general'));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'warning';
      case 'preparing':
        return 'info';
      case 'ready':
        return 'primary';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'paid':
        return 'success';
      default:
        return 'default';
    }
  };


  const calculateTotalItems = (order: OrderOut) => {
    return order.items.reduce((total, item) => total + item.quantity, 0);
  };

  if (!canAccessAllEvents(user)) {
    return (
      <div className="order-management-page">
        <div className="container">
          <Card variant="elevated" padding="lg">
            <h1>{t('errors.unauthorized')}</h1>
            <p>{t('management.orders.organizerOnly')}</p>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="order-management-page">
      <div className="container">
        <div className="order-management-page__header">
          <h1 className="order-management-page__title">
            {t('management.orders.title')}
          </h1>
          <p className="order-management-page__description">
            {t('management.orders.description')}
          </p>
        </div>

        {error && (
          <Card variant="elevated" padding="md" className="order-management-page__error">
            <p>{error}</p>
          </Card>
        )}

        {/* Filters */}
        <Card variant="elevated" padding="lg" className="order-management-page__filters">
          <div className="order-management-page__filters-header">
            <h3>{t('management.orders.filters')}</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? t('common.hide') : t('common.show')} {t('management.orders.filters')}
            </Button>
          </div>

          {showFilters && (
            <div className="order-management-page__filters-content">
              <div className="order-management-page__filters-grid">
                <SearchableSelect<number>
                  name="event_id"
                  label={t('management.orders.filterByEvent')}
                  placeholder={t('management.orders.allEvents')}
                  clearable
                  options={events.map((event) => ({ value: event.id, label: event.name }))}
                  value={filters.event_id || null}
                  onChange={(value) => handleFilterChange('event_id', value ?? undefined)}
                />

                {filters.event_id && (
                  <FormField
                    type="select"
                    name="occurrence_start"
                    label={t('management.orders.filterByNight')}
                    value={filters.occurrence_start || ''}
                    onChange={(e) => handleFilterChange('occurrence_start', e.target.value || undefined)}
                  >
                    <option value="">{t('management.orders.allNights')}</option>
                    {nights.map((night) => (
                      <option key={night.occurrence_start} value={night.occurrence_start}>
                        {formatNight(night.occurrence_start)} · {t('management.orders.nightOrders', { count: night.order_count })}
                      </option>
                    ))}
                  </FormField>
                )}

                <FormField
                  type="select"
                  name="status"
                  label={t('management.orders.filterByStatus')}
                  value={filters.status || ''}
                  onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
                >
                  <option value="">{t('management.orders.allStatuses')}</option>
                  <option value="pending">{t('orderConfirmation.statuses.pending')}</option>
                  <option value="preparing">{t('orderConfirmation.statuses.preparing')}</option>
                  <option value="ready">{t('orderConfirmation.statuses.ready')}</option>
                  <option value="completed">{t('orderConfirmation.statuses.completed')}</option>
                  <option value="cancelled">{t('orderConfirmation.statuses.cancelled')}</option>
                  <option value="paid">{t('orderConfirmation.statuses.paid')}</option>
                </FormField>

                <FormField
                  type="date"
                  name="date_from"
                  label={t('management.orders.dateFrom')}
                  value={filters.date_from || ''}
                  onChange={(e) => handleFilterChange('date_from', e.target.value || undefined)}
                />

                <FormField
                  type="date"
                  name="date_to"
                  label={t('management.orders.dateTo')}
                  value={filters.date_to || ''}
                  onChange={(e) => handleFilterChange('date_to', e.target.value || undefined)}
                />
              </div>

              <div className="order-management-page__filters-actions">
                <Button
                  variant="primary"
                  onClick={handleApplyFilters}
                >
                  {t('management.orders.applyFilters')}
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleClearFilters}
                >
                  {t('management.orders.clearFilters')}
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* Sales of the selected night */}
        {nightSummary && (
          <Card variant="elevated" padding="lg" className="order-management-page__night">
            <h3 className="order-management-page__night-title">
              {t('management.orders.nightSummary.title', { night: formatNight(nightSummary.occurrence_start) })}
            </h3>
            <div className="order-management-page__night-stats">
              <div className="order-management-page__night-stat">
                <span>{t('management.orders.nightSummary.revenue')}</span>
                <strong>{formatPrice(nightSummary.total_amount)}</strong>
              </div>
              <div className="order-management-page__night-stat">
                <span>{t('management.orders.nightSummary.sold')}</span>
                <strong>{nightSummary.sold_count}</strong>
              </div>
              <div className="order-management-page__night-stat">
                <span>{t('management.orders.nightSummary.pending')}</span>
                <strong>{nightSummary.pending_count}</strong>
              </div>
              <div className="order-management-page__night-stat">
                <span>{t('management.orders.nightSummary.cancelled')}</span>
                <strong>{nightSummary.cancelled_count}</strong>
              </div>
            </div>
            <div className="order-management-page__night-lists">
              <div>
                <h4>{t('management.orders.nightSummary.byBar')}</h4>
                {nightSummary.by_venue.length === 0 ? (
                  <p className="order-management-page__night-empty">{t('management.orders.nightSummary.noSales')}</p>
                ) : (
                  <ul>
                    {nightSummary.by_venue.map((venue) => (
                      <li key={venue.event_venue_id ?? 'none'}>
                        <span>{venue.name || t('management.orders.nightSummary.noBar')}</span>
                        <span>{t('management.orders.nightOrders', { count: venue.order_count })}</span>
                        <strong>{formatPrice(venue.total_amount)}</strong>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <h4>{t('management.orders.nightSummary.topProducts')}</h4>
                {nightSummary.top_products.length === 0 ? (
                  <p className="order-management-page__night-empty">{t('management.orders.nightSummary.noSales')}</p>
                ) : (
                  <ul>
                    {nightSummary.top_products.map((product) => (
                      <li key={product.name ?? 'none'}>
                        <span>{product.name}</span>
                        <span>x{product.quantity}</span>
                        <strong>{formatPrice(product.total_amount)}</strong>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* Orders List */}
        <div className="order-management-page__orders">
          {loading ? (
            <Card variant="elevated" padding="lg">
              <p>{t('common.loading')}</p>
            </Card>
          ) : orders.length === 0 ? (
            <Card variant="elevated" padding="lg">
              <p>{t('management.orders.noOrders')}</p>
            </Card>
          ) : (
            <Card variant="elevated" padding="none" className="order-management-page__orders-table-card">
              <div className="order-management-page__table-wrapper">
                <table className="order-management-page__table">
                  <thead>
                    <tr>
                      <th>{t('management.orders.orderNumber')}</th>
                      <th>{t('management.orders.status')}</th>
                      <th>{t('management.orders.createdAt')}</th>
                      <th>{t('management.orders.items')}</th>
                      <th>{t('management.orders.total')}</th>
                      <th className="order-management-page__table-actions-col">{t('management.orders.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr key={order.id}>
                        <td>#{order.id}</td>
                        <td>
                          <span className={`order-management-page__status-badge order-management-page__status-badge--${getStatusColor(order.status)}`}>
                            {t(`orderConfirmation.statuses.${order.status.toLowerCase()}`)}
                          </span>
                        </td>
                        <td>{formatOrderDate(order.created_at)}</td>
                        <td>{calculateTotalItems(order)} {t('management.orders.items')}</td>
                        <td>{formatPrice(order.total_amount)}</td>
                        <td>
                          <div className="order-management-page__table-actions-cell">
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => setSelectedOrder(order)}
                            >
                              {t('management.orders.viewDetails')}
                            </Button>

                            {order.status === 'pending' && (
                              <Button
                                variant="success"
                                size="sm"
                                onClick={() => handleUpdateOrderStatus(order.id, 'paid')}
                              >
                                {t('management.orders.markPaid')}
                              </Button>
                            )}

                            {order.status === 'paid' && (
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => handleUpdateOrderStatus(order.id, 'preparing')}
                              >
                                {t('management.orders.startPreparing')}
                              </Button>
                            )}

                            {order.status === 'preparing' && (
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => handleUpdateOrderStatus(order.id, 'ready')}
                              >
                                {t('management.orders.markReady')}
                              </Button>
                            )}

                            {order.status === 'ready' && (
                              <Button
                                variant="success"
                                size="sm"
                                onClick={() => handleUpdateOrderStatus(order.id, 'completed')}
                              >
                                {t('management.orders.markDelivered')}
                              </Button>
                            )}

                            {order.status === 'pending' && (
                              <Button
                                variant="error"
                                size="sm"
                                onClick={() => handleUpdateOrderStatus(order.id, 'cancelled')}
                              >
                                {t('management.orders.cancel')}
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>

        {/* Order Details Modal */}
        {selectedOrder && (
          <div className="order-management-page__modal-overlay" onClick={() => setSelectedOrder(null)}>
            <div className="order-management-page__modal" onClick={(e) => e.stopPropagation()}>
              <div className="order-management-page__modal-header">
                <h2>{t('management.orders.orderDetails')} #{selectedOrder.id}</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedOrder(null)}
                >
                  ×
                </Button>
              </div>
              
              <div className="order-management-page__modal-content">
                <div className="order-management-page__modal-section">
                  <h3>{t('management.orders.orderInfo')}</h3>
                  <p><strong>{t('management.orders.status')}:</strong> {t(`orderConfirmation.statuses.${selectedOrder.status.toLowerCase()}`)}</p>
                  <p><strong>{t('management.orders.createdAt')}:</strong> {formatDetailedOrderDate(selectedOrder.created_at)}</p>
                  <p><strong>{t('management.orders.updatedAt')}:</strong> {formatDetailedOrderDate(selectedOrder.updated_at)}</p>
                  <p><strong>{t('bartender.orderDetail.event')}:</strong> {selectedOrder.event_name || '—'}</p>
                  <p><strong>{t('bartender.orderDetail.venue')}:</strong> {selectedOrder.venue_name || t('orderActivity.noVenue')}</p>
                  <p>
                    <strong>{selectedOrder.status === 'completed' ? t('orderActivity.deliveredBy') : t('orderActivity.openedBy')}:</strong>{' '}
                    {selectedOrder.claimed_by_name || '—'}
                  </p>
                </div>

                <div className="order-management-page__modal-section">
                  <h3>{t('management.orders.items')}</h3>
                  {selectedOrder.items.map((item, index) => (
                    <div key={index} className="order-management-page__modal-item">
                      <div className="order-management-page__modal-item-info">
                        <span className="order-management-page__modal-item-name">
                          {item.drink_name || item.food_name}
                        </span>
                        <span className="order-management-page__modal-item-quantity">
                          x{item.quantity}
                        </span>
                      </div>
                      <div className="order-management-page__modal-item-price">
                        <span>{formatWithTax(item.price_at_time, item.tax_rate_at_time)} x1</span>
                        <strong>{formatWithTax(item.price_at_time * item.quantity, item.tax_rate_at_time)}</strong>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="order-management-page__modal-section">
                  <h3>{t('bartender.orderDetail.activityLog')}</h3>
                  <OrderActivityLog orderId={selectedOrder.id} refreshKey={selectedOrder.updated_at} />
                </div>

                <div className="order-management-page__modal-section">
                  <h3>{t('management.orders.total')}</h3>
                  <div className="order-management-page__modal-total">
                    <span>{t('cart.subtotal')}: {formatPrice(selectedOrder.subtotal)}</span>
                    <span>{t('cart.tax')}: {formatPrice(selectedOrder.total_tax)}</span>
                    <strong>{t('cart.total')}: {formatPrice(selectedOrder.total_amount)}</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderManagement; 